import { merge as _merge, cloneDeep, groupBy, isEqual, last } from 'lodash-es'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'

import { useUsersStore } from 'src/stores/users.js'

import { updateProfileJSON } from 'src/api/user.js'
import { pickRecent } from 'src/services/sync_storage/sync_storage.js'

export const defaultState = {
  // do we need to update data on server?
  dirty: false,
  highlight: {
    _journal: [],
  },
  // raw data
  raw: null,
  // local cache
  cache: null,
}

const _wrapData = (data, userName) => {
  return {
    ...data,
    _user: userName,
    _timestamp: Date.now(),
  }
}

const _checkValidity = (data) => data._timestamp > 0

export const _getRecentData = (cache, live) => {
  const result = pickRecent(cache, live, _checkValidity)
  const merge = (a, b) => {
    return {
      _user: a._user ?? b._user,
      _timestamp: a._timestamp ?? b._timestamp,
      needUpload: b.needUpload ?? a.needUpload,
      highlight: _merge(cloneDeep(a.highlight), b.highlight),
    }
  }

  // the defaults fill in what a copy lacks
  result.recent = result.recent && merge(cloneDeep(defaultState), result.recent)
  result.stale = result.stale && merge(cloneDeep(defaultState), result.stale)

  return result
}

const _mergeJournal = (...journals) => {
  // Ignore invalid journal entries
  const allJournals = journals
    .map((j) => (Array.isArray(j) ? j : []))
    .flat()
    .filter(
      (entry) =>
        Object.hasOwn(entry, 'user') &&
        Object.hasOwn(entry, 'operation') &&
        Object.hasOwn(entry, 'args') &&
        Object.hasOwn(entry, 'timestamp'),
    )
  const grouped = groupBy(allJournals, 'user')
  const trimmedGrouped = Object.entries(grouped).map(([user, journal]) => {
    // side effect
    journal.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1))

    return [last(journal)]
  })
  return trimmedGrouped
    .flat()
    .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1))
}

export const _mergeHighlights = (recent, stale) => {
  if (!stale) return recent
  if (!recent) return stale
  const { _journal: recentJournal, ...recentHighlight } = recent
  const { _journal: staleJournal } = stale
  /** Journal entry format:
   * user: user to entry in highlight storage
   * timestamp: timestamp of the change
   * operation: operation type
   * arguments: array of arguments, depends on operation type
   *
   * currently only supported operation type is "set" which just sets the value
   * to requested one. Intended only to be used with simple preferences (boolean, number)
   */
  const resultHighlight = { ...recentHighlight }
  const Journal = _mergeJournal(staleJournal, recentJournal)
  Journal.forEach(({ user, operation, args }) => {
    if (user.startsWith('_')) {
      throw new Error(
        `journal contains entry to edit internal (starts with _) field '${user}', something is incorrect here, ignoring.`,
      )
    }
    switch (operation) {
      case 'set':
        resultHighlight[user] = args[0]
        break
      case 'unset':
        delete resultHighlight[user]
        break
      default:
        return console.error(`Unknown journal operation: '${operation}'`)
    }
  })
  return { ...resultHighlight, _journal: Journal }
}

export const useUserHighlightStore = defineStore('user_highlight', {
  state() {
    return cloneDeep(defaultState)
  },
  actions: {
    setAndSave({ user, value }) {
      this.set({ user, value })
      this.pushHighlight()
    },
    unsetAndSave({ user }) {
      this.unset({ user })
      this.pushHighlight()
    },
    get(user) {
      const present = this.highlight[user] || {}
      return {
        user,
        type: 'disabled',
        color: '#FFFFFF',
        ...present,
      }
    },
    set({ user, value }) {
      if (user.startsWith('_')) {
        throw new Error(
          `Tried to edit internal (starts with _) field '${user}', ignoring.`,
        )
      }
      const oldValue = this.highlight[user]
      const newValue = {
        user,
        ...oldValue,
        ...value,
      }
      this.highlight[user] = newValue
      this.highlight._journal = [
        ...this.highlight._journal,
        { operation: 'set', user, args: [newValue], timestamp: Date.now() },
      ]
      this.dirty = true
    },
    unset({ user, value }) {
      if (user.startsWith('_')) {
        throw new Error(
          `Tried to edit internal (starts with _) field '${user}', ignoring.`,
        )
      }
      delete this.highlight[user]
      this.highlight._journal = [
        ...this.highlight._journal,
        { operation: 'unset', user, args: [], timestamp: Date.now() },
      ]
      this.dirty = true
    },
    updateCache({ username }) {
      this.highlight._journal = _mergeJournal(this.highlight._journal)
      this.cache = _wrapData(
        {
          highlight: toRaw(this.highlight),
        },
        username,
      )
    },
    async initUserHighlight(userData) {
      const live = userData.user_highlight
      this.raw = live
      let cache = this.cache
      if (cache?._user !== userData.fqn) {
        console.warn(
          'Cache belongs to another user! reinitializing local cache!',
        )
        cache = null
      }

      let { recent, stale, needUpload } = _getRecentData(cache, live)

      let dirty = false

      if (recent === null) {
        console.debug('[HIGHLIGHT] Data is empty, initializing')
        recent = _wrapData({
          highlight: { ...defaultState.highlight },
        })
      }

      if (!needUpload && recent && stale) {
        console.debug('[HIGHLIGHT] Checking if data needs merging...')
        // discarding timestamps
        const { _timestamp: _0, ...recentData } = recent
        const { _timestamp: _2, ...staleData } = stale
        dirty = !isEqual(recentData, staleData)
        console.debug(
          `[HIGHLIGHT] Data ${dirty ? 'needs' : "doesn't need"} merging`,
        )
      }

      let highlights
      if (dirty) {
        console.debug('[HIGHLIGHT] Merging the data...')
        highlights = _mergeHighlights(
          recent.highlight ?? {},
          stale.highlight ?? {},
        )
      } else {
        highlights = recent.highlight
      }

      recent.highlight = { ...defaultState.highlight, ...highlights }

      this.dirty = dirty || needUpload
      this.cache = recent
      // set local timestamp to smaller one if we don't have any changes
      if (stale && recent && !this.dirty) {
        this.cache._timestamp = Math.min(stale._timestamp, recent._timestamp)
      }
      this.highlight = this.cache.highlight
    },
    pushHighlight({ force = false } = {}) {
      const needPush = this.dirty || force
      if (!needPush) return
      this.updateCache({ username: useUsersStore().currentUser.fqn })
      const params = {
        pleroma_settings_store: { user_highlight: this.cache },
      }
      updateProfileJSON({
        params,
      }).then(({ data: user }) => {
        this.initUserHighlight(user)
        this.dirty = false
      })
    },
  },
  persist: {},
})
